import { useTranslations } from "adapters/i18n";
import { ConfigurationItem } from "constants/configurations";
import { useMemo } from "react";
import type {
  ConfigurationCardItem,
  ConfigurationItemsVisibility,
} from "../configurations.types";
import { ConfigurationFeature } from "../utils/configurations.utils";

const featureToItemsMap: Record<ConfigurationFeature, ConfigurationItem[]> = {
  [ConfigurationFeature.ALL]: Object.values(
    ConfigurationItem
  ) as ConfigurationItem[],
  [ConfigurationFeature.ARROBA_BALANCE]: [
    ConfigurationItem.ANIMAL_CATEGORY,
    ConfigurationItem.WEIGHING_BY_CATEGORY,
  ],
  [ConfigurationFeature.BIRTH]: [ConfigurationItem.ANIMAL_CATEGORY],
  [ConfigurationFeature.CONCILIATION]: [ConfigurationItem.ANIMAL_CATEGORY],
  [ConfigurationFeature.CONSUMPTION_CLOSURE]: [
    ConfigurationItem.FORMULATIONS,
    ConfigurationItem.INGREDIENTS,
  ],
  [ConfigurationFeature.CREEP_FEEDING]: [
    ConfigurationItem.ANIMAL_CATEGORY,
    ConfigurationItem.FORMULATIONS,
  ],
  [ConfigurationFeature.DEATH]: [
    ConfigurationItem.ANIMAL_CATEGORY,
    ConfigurationItem.CAUSE_OF_DEATH,
  ],
  [ConfigurationFeature.DRUM]: [ConfigurationItem.FORMULATIONS],
  [ConfigurationFeature.ENTRIES_AND_EXITS]: [ConfigurationItem.ANIMAL_CATEGORY],
  [ConfigurationFeature.FARM_GOALS]: [ConfigurationItem.FARM_GOALS],
  [ConfigurationFeature.HERD_EVOLUTION_AND_NUTRITION_PLAN]: [
    ConfigurationItem.HERD_EVOLUTION_AND_NUTRITION_PLAN,
  ],
};

type UseConfigurationItemsProps = {
  itemsVisibility: ConfigurationItemsVisibility;
  selectedFeature: ConfigurationFeature;
};

export function useConfigurationItems({
  itemsVisibility,
  selectedFeature,
}: UseConfigurationItemsProps) {
  const { t } = useTranslations();

  const items = useMemo<Record<ConfigurationItem, ConfigurationCardItem>>(
    () => ({
      [ConfigurationItem.ANIMAL_CATEGORY]: {
        id: ConfigurationItem.ANIMAL_CATEGORY,
        label: t("animalCategory:animal-category"),
        description: t("configurations:configure-animal-category-description"),
        hasNewBadge: false,
      },
      [ConfigurationItem.CAUSE_OF_DEATH]: {
        id: ConfigurationItem.CAUSE_OF_DEATH,
        label: t("livestock:cause-of-death"),
        description: t("configurations:configure-cause-of-death-description"),
        hasNewBadge: false,
      },
      [ConfigurationItem.CORRAL_LISTING]: {
        id: ConfigurationItem.CORRAL_LISTING,
        label: t("locales:corral-listing"),
        description: t("configurations:configure-corral-listing-description"),
        hasNewBadge: false,
      },
      [ConfigurationItem.FARM_GOALS]: {
        id: ConfigurationItem.FARM_GOALS,
        label: t("locales:farm-goals"),
        description: t("configurations:configure-farm-goals-description"),
        hasNewBadge: false,
      },
      [ConfigurationItem.FORMULATIONS]: {
        id: ConfigurationItem.FORMULATIONS,
        label: t("formulation:formulations"),
        description: t("configurations:configure-formulations-description"),
        hasNewBadge: false,
      },
      [ConfigurationItem.HERD_EVOLUTION_AND_NUTRITION_PLAN]: {
        id: ConfigurationItem.HERD_EVOLUTION_AND_NUTRITION_PLAN,
        label: t("planning:herd-evolution-and-nutrition-plan"),
        description: t("configurations:configure-herd-evolution-description"),
        hasNewBadge: false,
      },
      [ConfigurationItem.INGREDIENTS]: {
        id: ConfigurationItem.INGREDIENTS,
        label: t("ingredients:ingredients"),
        description: t("configurations:configure-ingredients-description"),
        hasNewBadge: false,
      },
      [ConfigurationItem.LOT_GOALS]: {
        id: ConfigurationItem.LOT_GOALS,
        label: t("livestock:lot-goals"),
        description: t("configurations:configure-plot-goals-description"),
        hasNewBadge: false,
      },
      [ConfigurationItem.PLOT_LISTING]: {
        id: ConfigurationItem.PLOT_LISTING,
        label: t("livestock:lot-listing"),
        description: t("configurations:configure-plot-listing-description"),
        hasNewBadge: false,
      },
      [ConfigurationItem.RAIN_HISTORY]: {
        id: ConfigurationItem.RAIN_HISTORY,
        label: t("locales:rain-history"),
        description: t("configurations:configure-rain-history-description"),
        hasNewBadge: false,
      },
      [ConfigurationItem.SCORE_ADJUSTMENT]: {
        id: ConfigurationItem.SCORE_ADJUSTMENT,
        label: t("locales:score-adjustment"),
        description: t("configurations:configure-score-adjustment-description"),
        hasNewBadge: false,
      },
      [ConfigurationItem.WEIGHING_BY_CATEGORY]: {
        id: ConfigurationItem.WEIGHING_BY_CATEGORY,
        label: t("animalCategory:weighing-by-category"),
        description: t(
          "configurations:configure-weighing-by-category-description"
        ),
        hasNewBadge: false,
      },
      [ConfigurationItem.WEIGHT_GAIN_CURVE]: {
        id: ConfigurationItem.WEIGHT_GAIN_CURVE,
        label: t("livestock:weight-gain-curve"),
        description: t(
          "configurations:configure-weight-gain-curve-description"
        ),
        hasNewBadge: false,
      },
    }),
    [t]
  );

  const itemToVisibilityMap: Partial<Record<ConfigurationItem, boolean>> = {
    [ConfigurationItem.CAUSE_OF_DEATH]: itemsVisibility.showCauseOfDeath,
    [ConfigurationItem.FORMULATIONS]:
      itemsVisibility.showFormulationRegistration,
    [ConfigurationItem.HERD_EVOLUTION_AND_NUTRITION_PLAN]:
      itemsVisibility.showHerdEvolutionAndNutritionPlan,
    [ConfigurationItem.LOT_GOALS]: itemsVisibility.showLotGoal,
    [ConfigurationItem.RAIN_HISTORY]: itemsVisibility.showRainHistory,
    [ConfigurationItem.SCORE_ADJUSTMENT]: itemsVisibility.showScoreAdjustment,
    [ConfigurationItem.WEIGHT_GAIN_CURVE]: itemsVisibility.showWeightGain,
  };

  const isItemVisible = (item: ConfigurationItem) => {
    const itemsForFeature = featureToItemsMap[selectedFeature];
    const isVisibleByFeature = itemsForFeature.includes(item);
    const isVisible = itemToVisibilityMap[item] ?? true;
    return isVisibleByFeature && isVisible;
  };

  const sortedItems = Object.values(items)
    .filter((item) => isItemVisible(item.id))
    .sort((a, b) => a.label.localeCompare(b.label));

  return { configurationItems: sortedItems };
}
